import { useState, useMemo, useEffect } from 'react';
import { Check, Search } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/core/components/ui/select';
import { Input } from '@/core/components/ui/input';
import { ActorTree } from '../../features/site-management/types/actor-types';
import { useActorStore } from '@/features/site-management/stores/actor-store';

interface LocationSelectorProps {
  operatorId: number;
  selectedLocationId?: string;
  onLocationChange: (locationId: string) => void;
  className?: string;
  selectedActorId?: string;
  skipApiCallOnActorChange?: boolean;
  isActorsLoading?: boolean;
  autoSelectFirst?: boolean;
  disabled?: boolean;
}

export function LocationSelector({
  operatorId,
  selectedLocationId = '',
  onLocationChange,
  className = '',
  selectedActorId,
  skipApiCallOnActorChange = false,
  isActorsLoading = false,
  autoSelectFirst = true,
  disabled = false
}: LocationSelectorProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [_isOpen, setIsOpen] = useState(false);
  const { actors: actorTrees, isLoading, error, loadActorTree } = useActorStore();

  // Load locations when operatorId changes
  useEffect(() => {
    if (operatorId && !skipApiCallOnActorChange) {
      loadActorTree(operatorId.toString());
    }
  }, [operatorId, selectedActorId, skipApiCallOnActorChange, loadActorTree]);

  // Get all descendants of a parent actor (recursive: children, grandchildren, etc.)
  const getAllDescendants = (parentId: number, actors: ActorTree[]): ActorTree[] => {
    const directChildren = actors.filter(actor => actor.parentActorId === parentId);
    const descendants = [...directChildren];

    // Recursively get all descendants
    directChildren.forEach(child => {
      descendants.push(...getAllDescendants(child.actorId, actors));
    });

    return descendants;
  };

  // Compute locations to show - get ALL descendants recursively
  const locations = useMemo(() => {
    if (!actorTrees || actorTrees.length === 0) return [];

    // Get all descendants of the operator (includes children, grandchildren, etc.)
    const allDescendants = getAllDescendants(operatorId, actorTrees);

    if (allDescendants.length > 0) {
      return allDescendants.sort((a, b) =>
        (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
      );
    }

    // For non-admin users: find root actors
    const actorIds = new Set(actorTrees.map(a => a.actorId));
    const rootActors = actorTrees.filter(actor => !actorIds.has(actor.parentActorId));

    return rootActors.sort((a, b) =>
      (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase())
    );
  }, [actorTrees, operatorId]);

  // Auto-select first location if enabled
  useEffect(() => {
    if (autoSelectFirst && !selectedLocationId && locations.length > 0 && !isLoading) {
      onLocationChange(locations[0].actorId.toString());
    }
  }, [autoSelectFirst, selectedLocationId, locations, isLoading, onLocationChange]);

  // Filter locations based on search query
  const filteredLocations = useMemo(() => {
    if (!searchQuery) return locations;

    const query = searchQuery.toLowerCase();
    return locations.filter(location =>
      location.name?.toLowerCase().includes(query) ||
      location.actorId?.toString().includes(query)
    );
  }, [locations, searchQuery]);

  // Get selected location display text
  const selectedLocation = useMemo(() => {
    if (!selectedLocationId) return autoSelectFirst ? 'Select location...' : 'All Locations';
    const location = locations.find(loc => loc.actorId.toString() === selectedLocationId);
    return location?.name || (autoSelectFirst ? 'Select location...' : 'All Locations');
  }, [selectedLocationId, locations, autoSelectFirst]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery(''); // Reset search when closing
    }
  };

  const handleSelect = (value: string) => {
    if (value === 'all') {
      onLocationChange('');
    } else {
      onLocationChange(value);
    }
  };

  if (error) {
    return (
      <div className={cn("w-48", className)}>
        <div className="text-xs text-red-600">Failed to load locations</div>
      </div>
    );
  }

  return (
    <Select
      value={selectedLocationId || 'all'}
      onValueChange={handleSelect}
      onOpenChange={handleOpenChange}
      disabled={disabled || isLoading || isActorsLoading}
    >
      <SelectTrigger className={cn("w-48", className)}>
        <SelectValue>
          {isLoading || isActorsLoading
            ? (isActorsLoading ? "Switching operator..." : "Loading locations...")
            : selectedLocation}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-80">
        {/* Search Input */}
        <div className="flex items-center gap-2 px-2 pb-2 border-b sticky top-0 bg-white z-10">
          <Search className="h-4 w-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Type to search locations..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="h-8 border-0 focus-visible:ring-0 focus-visible:ring-offset-0 px-0 text-sm"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </div>

        {/* All Locations Option (only if autoSelectFirst is false) */}
        {!autoSelectFirst && (
          <SelectItem value="all" className="font-medium">
            <div className="flex items-center justify-between w-full">
              <div className="flex flex-col">
                <span className="font-medium">All Locations</span>
                <span className="text-xs text-gray-500">Show data from all locations</span>
              </div>
              {!selectedLocationId && (
                <Check className="h-4 w-4 text-primary ml-2 flex-shrink-0" />
              )}
            </div>
          </SelectItem>
        )}

        {/* Filtered Locations */}
        {filteredLocations.length > 0 ? (
          filteredLocations.map((location) => (
            <SelectItem
              key={location.actorId}
              value={location.actorId.toString()}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex flex-col">
                  <span className="font-medium">{location.name}</span>
                  <span className="text-xs text-gray-500">ID: {location.actorId}</span>
                </div>
                {selectedLocationId === location.actorId.toString() && (
                  <Check className="h-4 w-4 text-primary ml-2 flex-shrink-0" />
                )}
              </div>
            </SelectItem>
          ))
        ) : (
          <div className="px-2 py-4 text-center text-sm text-gray-500">
            No locations found
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
